import { defineStore } from "pinia";
import _ from "lodash";
import * as expenseService from "@/shared/api/expenseService";
import * as storeHelper from "@/shared/storeHelper";
import Vue from "vue";
import {
    FetchState,
    HsabExpenseEvidenceShoeboxInitialState,
    HsabExpenseEvidenceShoeboxModalSource,
    ManagedExpensePagedResponse
} from "@/shared/types";
import { useNotifierStore } from "@/shared/store/notifier";
import { openUrl } from "@/shared/urlHelper";

export const initialState: HsabExpenseEvidenceShoeboxInitialState = {
    currentPage: {
        fetchState: FetchState.Uninitialized,
        page: 1,
        pageSize: 5,
        totalRecords: 0,
        numberOfPages: null,
        records: [],
    },
    isModalOpen: false,
    modalOpenedBy: HsabExpenseEvidenceShoeboxModalSource.none,
    isBrowsingUploadedFiles: false,
};

export const useHsabExpenseEvidenceShoeboxStore = defineStore("hsabExpenseEvidenceShoebox", {
    state: (): HsabExpenseEvidenceShoeboxInitialState => (_.cloneDeep(initialState)),
    getters: {
        isCurrentPageLoaded: state => state.currentPage.fetchState === FetchState.Loaded,
        isCurrentPageLoading: state => state.currentPage.fetchState === FetchState.Loading,
        currentPageItems: state => state.currentPage.records,
        currentPageNumber: state => state.currentPage.page,
        totalItemCount: state => state.currentPage.totalRecords,
        totalPageCount: state => state.currentPage.numberOfPages !== null ? state.currentPage.numberOfPages : 0,
    },
    actions: {
        overwriteRecordsForPage (response: ManagedExpensePagedResponse) {
            for (let index = this.currentPage.pageSize - 1; index >= 0; index--) {
                if (index < response.records.length) {
                    Vue.set(this.currentPage.records, index, response.records[index]);
                } else {
                    Vue.delete(this.currentPage.records, index);
                }
            }
        },
        initializeData () {
            if (this.currentPage.fetchState === FetchState.Loaded) {
                return;
            } else {
                this.loadPage(1);
            }
        },
        async loadPage (page: number) {
            if (this.currentPage.fetchState === FetchState.Loading) {
                return Promise.resolve();
            }

            this.currentPage.fetchState = FetchState.Loading;

            return expenseService.getHsabExpenseEvidenceShoeboxByPage(page, this.currentPage.pageSize)
                .then(response => {
                    this.currentPage.page = page;
                    this.currentPage.totalRecords = response.data.totalRecords;
                    this.currentPage.numberOfPages = response.data.numberOfPages;
                    this.overwriteRecordsForPage(response.data);
                    this.currentPage.fetchState = FetchState.Loaded;
                })
                .catch(err => {
                    this.currentPage.fetchState = FetchState.Failed;
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        async OpenAttachment (shoeboxId: number) {
            const newTab = window.open();
            return expenseService.getHsabExpenseEvidenceShoeboxSignedS3Url(shoeboxId)
                .then(response => {
                        openUrl(response.data);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: false });
                    newTab.close();
                });
        },
        async uploadShoeboxFile (files: File[]) {
            return expenseService.createAndUploadShoeboxFiles(files).then(response => {
                const notifierStore = useNotifierStore();
                notifierStore.setSuccess("Your receipt has been successfully uploaded.", 5000);
                if (response.data.errors && response.data.errors.length > 0) {
                    notifierStore.setDanger("Could not upload receipt file(s)");
                }
            })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        deleteShoeboxFile (shoeBoxFileId: number) {
            return expenseService.deleteShoeboxFile(shoeBoxFileId)
            .then(response => {
                const notifierStore = useNotifierStore();
                notifierStore.setSuccess("Your receipt has been successfully deleted.", notifierStore.shortTimeout);
                if (!response.data) {
                    notifierStore.setDanger("Could not delete receipt", notifierStore.shortTimeout);
                }
            })
            .catch(err => storeHelper.handleError({ error: err, rethrow: true, timeout: 5000 }));
        },
        openShoeboxModal (source: HsabExpenseEvidenceShoeboxModalSource) {
            this.initializeData();
            this.modalOpenedBy = source;
            this.isModalOpen = true;
        },
        closeShoeboxModal () {
            this.isBrowsingUploadedFiles = false;
            this.isModalOpen = false;
            this.modalOpenedBy = HsabExpenseEvidenceShoeboxModalSource.none;
        },
    }
});